import styled, { css } from 'styled-components';

// CSS Variables as constants
const colors = {
  ink: '#1A1814',
  ink2: '#221F1A',
  cream: '#F3ECE1',
  cream2: '#EBE3D6',
  red: '#D9442B',
  body: '#4A453D',
  muted: '#6B645A',
  faint: '#8A8276',
  rule: '#D2C8B8',
  line: '#3A342C',
  text: '#CFC6B8',
  dim: '#A89E8F',
};

const fonts = {
  display: "'Instrument Serif', Georgia, serif",
  serif: "'Newsreader', Georgia, serif",
  mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  ui: "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
};

export const PageWrapper = styled.div`
  background: ${colors.cream};
  min-height: 100vh;
  padding-top: 88px;

  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

export const Wrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
`;

export const Eyebrow = styled.p`
  font: 400 12px ${fonts.mono};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${colors.red};
  margin: 0;
`;

// Hero Section
export const HeroSection = styled.section`
  padding: 88px 0 56px;
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: 64px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-top: 56px;
  }
`;

export const HeroTitle = styled.h1`
  margin-top: 22px;
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(52px, 6.4vw, 92px);
  line-height: 0.95;
  letter-spacing: -0.015em;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const HeroSub = styled.p`
  margin: 0;
  font-size: 20px;
  color: ${colors.body};
  max-width: 27em;
  font-family: ${fonts.serif};
  line-height: 1.55;
`;

export const HeroMeta = styled.div`
  margin-top: 22px;
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.muted};

  b {
    color: ${colors.ink};
    font-weight: 400;
  }
`;

// Featured Pick Section
export const PickSection = styled.section`
  background: ${colors.ink};
  color: ${colors.cream};
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 48px;
  align-items: center;
  padding: 40px 44px;
  position: relative;
  border-top: 3px solid ${colors.red};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 22px;
    padding: 32px 24px 28px;
  }
`;

export const PickDate = styled.div`
  text-align: center;
  border-right: 1px solid ${colors.line};
  padding-right: 48px;

  @media (max-width: 900px) {
    text-align: left;
    border-right: 0;
    padding-right: 0;
    display: flex;
    align-items: baseline;
    gap: 14px;
  }
`;

export const PickDay = styled.div`
  font: 400 88px/0.9 ${fonts.display};

  @media (max-width: 900px) {
    font-size: 64px;
  }
`;

export const PickMonth = styled.div`
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${colors.red};
  margin-top: 6px;

  @media (max-width: 900px) {
    margin-top: 0;
  }
`;

export const PickLabel = styled.div`
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${colors.red};
`;

export const Badge = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1.3px solid ${colors.red};
  position: relative;
  vertical-align: -2px;
  margin-right: 10px;

  &::after {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${colors.red};
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

export const PickTitle = styled.h2`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(32px, 3.2vw, 44px);
  line-height: 1.02;
  letter-spacing: -0.015em;
  margin: 10px 0 12px;
`;

export const PickDesc = styled.p`
  margin: 0;
  color: ${colors.text};
  font-size: 17px;
  max-width: 40em;
  font-family: ${fonts.serif};
  line-height: 1.55;
`;

export const PickWhere = styled.div`
  margin-top: 14px;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.dim};

  b {
    color: ${colors.cream};
    font-weight: 400;
  }
`;

// Buttons
const buttonBase = css`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font: 500 15px ${fonts.ui};
  text-decoration: none;
  padding: 17px 26px;
  transition: gap 0.2s;
  cursor: pointer;
  border: none;

  &:hover {
    gap: 18px;
  }

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Button = styled.a`
  ${buttonBase}
  background: ${colors.ink};
  color: ${colors.cream};

  .ar {
    color: ${colors.red};
  }
`;

export const ButtonLight = styled.a`
  ${buttonBase}
  background: ${colors.cream};
  color: ${colors.ink};

  .ar {
    color: ${colors.red};
  }

  @media (max-width: 900px) {
    justify-self: start;
  }
`;

export const TextLink = styled.a`
  font: 400 11.5px ${fonts.mono};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  border-bottom: 1px solid currentColor;
  padding-bottom: 3px;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }
`;

// Filters
export const FiltersContainer = styled.div`
  margin-top: 72px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border-bottom: 1px solid ${colors.ink};
  padding-bottom: 18px;
`;

export const FiltersLabel = styled.span`
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.muted};
  margin-right: 8px;
`;

interface ChipProps {
  $active?: boolean;
}

export const Chip = styled.button<ChipProps>`
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  background: ${props => props.$active ? colors.ink : 'transparent'};
  color: ${props => props.$active ? colors.cream : colors.body};
  border: 1px solid ${props => props.$active ? colors.ink : colors.rule};
  padding: 10px 14px 9px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.ink};
    color: ${props => props.$active ? colors.cream : colors.ink};
  }

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }
`;

export const ChipCount = styled.span`
  color: ${colors.red};
  margin-left: 6px;
`;

// Month Sections
export const MonthSection = styled.section`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 32px;
  padding-top: 44px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 6px;
    padding-top: 40px;
  }
`;

export const MonthHeading = styled.h3`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: 44px;
  line-height: 1;
  letter-spacing: -0.015em;
  margin: 0;
  position: sticky;
  top: 24px;
  align-self: start;

  span {
    display: block;
    font: 400 11px ${fonts.mono};
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${colors.muted};
    margin-top: 10px;
  }

  @media (max-width: 900px) {
    position: static;
    font-size: 36px;
  }
`;

export const EventsList = styled.div``;

// Event Row
interface EventRowProps {
  $isFirst?: boolean;
}

export const EventRow = styled.article<EventRowProps>`
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr) auto;
  gap: 28px;
  padding: 22px 0 24px;
  border-top: 1px solid ${props => props.$isFirst ? colors.ink : colors.rule};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const EventWhen = styled.div`
  font: 400 30px/1 ${fonts.display};
  letter-spacing: -0.01em;

  small {
    display: block;
    font: 400 10.5px ${fonts.mono};
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${colors.muted};
    margin-top: 8px;
    line-height: 1.5;
  }
`;

export const EventTitle = styled.h4`
  margin: 0;
  font: 400 28px/1.08 ${fonts.display};
  letter-spacing: -0.01em;
`;

export const EventStar = styled.span`
  font: 400 10px ${fonts.mono};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.cream};
  background: ${colors.red};
  padding: 4px 7px 3px;
  vertical-align: 6px;
  margin-left: 8px;
  white-space: nowrap;
`;

export const EventWhere = styled.div`
  margin-top: 6px;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.muted};
`;

export const EventDesc = styled.p`
  margin: 10px 0 0;
  font-size: 17px;
  color: ${colors.body};
  max-width: 40em;
  font-family: ${fonts.serif};
  line-height: 1.55;
`;

export const EventTags = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

interface TagProps {
  $free?: boolean;
}

export const Tag = styled.span<TagProps>`
  font: 400 9.5px ${fonts.mono};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${props => props.$free ? colors.red : colors.body};
  border: 1px solid ${props => props.$free ? colors.red : colors.rule};
  padding: 5px 8px 4px;
`;

export const TicketsButton = styled.a`
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${colors.ink};
  border: 1px solid ${colors.ink};
  padding: 12px 14px 11px;
  white-space: nowrap;
  display: inline-flex;
  gap: 10px;

  &:hover {
    background: ${colors.ink};
    color: ${colors.cream};
  }

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }

  span {
    color: ${colors.red};
  }

  @media (max-width: 900px) {
    justify-self: start;
    margin-top: 6px;
  }
`;

export const EmptyMessage = styled.p`
  padding: 40px 0;
  color: ${colors.muted};
  font-style: italic;
  font-family: ${fonts.serif};
`;

// Guide CTA Section
export const GuideCTASection = styled.section`
  margin: 96px 0 0;
  border-top: 1px solid ${colors.ink};
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 72px;
  align-items: center;
  padding-top: 72px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const GuideCTATitle = styled.h2`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(44px, 5vw, 72px);
  line-height: 0.96;
  letter-spacing: -0.015em;
  margin: 18px 0 0;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const GuideCTAText = styled.p`
  font-size: 19px;
  color: ${colors.body};
  margin: 24px 0 0;
  max-width: 31em;
  font-family: ${fonts.serif};
  line-height: 1.55;

  i {
    font-style: italic;
  }
`;

export const GuideCTAList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 28px 0 0;
  border-top: 1px solid ${colors.rule};
  max-width: 34em;
`;

export const GuideCTAItem = styled.li`
  display: grid;
  grid-template-columns: 40px 1fr;
  padding: 13px 0;
  border-bottom: 1px solid ${colors.rule};
  font-size: 17px;
  font-family: ${fonts.serif};

  b {
    font: 400 11px ${fonts.mono};
    letter-spacing: 0.14em;
    color: ${colors.red};
    padding-top: 5px;
  }
`;

export const BuyRow = styled.div`
  margin-top: 32px;
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
`;

export const PriceBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

export const PriceNow = styled.span`
  font: 400 44px/1 ${fonts.display};
`;

export const PriceWas = styled.span`
  font: 400 24px ${fonts.display};
  color: ${colors.faint};
  text-decoration: line-through;
  text-decoration-color: ${colors.red};
  text-decoration-thickness: 2px;
`;

export const PriceLabel = styled.span`
  font: 400 10.5px ${fonts.mono};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.muted};
  line-height: 1.5;
`;

export const GuideImage = styled.picture`
  img {
    width: 100%;
    height: auto;
    display: block;
    max-width: 520px;
    margin-left: auto;

    @media (max-width: 900px) {
      margin: 0;
    }
  }
`;

// Bragbooq Section
export const BragSection = styled.section`
  margin-top: 112px;
  background: ${colors.ink};
  color: ${colors.cream};
`;

export const BragInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 64px;
  align-items: center;
  padding-block: 88px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding-block: 64px;
    gap: 40px;
  }
`;

export const BragTitle = styled.h2`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(40px, 4.4vw, 64px);
  line-height: 0.98;
  letter-spacing: -0.015em;
  margin: 18px 0 0;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const BragText = styled.p`
  color: ${colors.text};
  font-size: 19px;
  margin: 22px 0 0;
  max-width: 30em;
  font-family: ${fonts.serif};
  line-height: 1.55;
`;

export const BragRow = styled.div`
  margin-top: 32px;
  display: flex;
  gap: 28px;
  align-items: center;
  flex-wrap: wrap;
`;

export const BragTextLink = styled.a`
  font: 400 11.5px ${fonts.mono};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  border-bottom: 1px solid currentColor;
  padding-bottom: 3px;
  color: ${colors.dim};

  &:hover {
    color: ${colors.cream};
  }

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }
`;

export const BragShot = styled.div`
  border: 1px solid ${colors.line};
  background: ${colors.ink2};
  padding: 10px;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

export const BragCaption = styled.figcaption`
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  font: 400 10.5px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.dim};
`;

// Footer Note Section
export const FootNote = styled.section`
  padding: 56px 0 88px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 64px;
  border-bottom: 1px solid ${colors.rule};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

export const FootNoteTitle = styled.h3`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: 34px;
  line-height: 1.05;
  letter-spacing: -0.015em;
  margin: 0;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const FootNoteText = styled.p`
  margin: 12px 0 18px;
  color: ${colors.body};
  font-size: 17px;
  font-family: ${fonts.serif};
`;

export const FootNoteSmall = styled.small`
  font-size: 14.5px;
  color: ${colors.muted};
  line-height: 1.6;
  display: block;
  font-family: ${fonts.serif};
`;

export const SiteFooter = styled.footer`
  padding: 28px 0 40px;
  display: flex;
  justify-content: space-between;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.muted};

  a {
    text-decoration: none;
    color: inherit;

    &:focus-visible {
      outline: 2px solid ${colors.red};
      outline-offset: 3px;
    }
  }
`;

// Past Events Section
export const PastEventsSection = styled.section`
  margin-top: 72px;
  border-top: 1px solid ${colors.rule};
  padding-top: 32px;
`;

export const PastEventsHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }
`;

export const PastEventsTitle = styled.h3`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: 32px;
  line-height: 1;
  letter-spacing: -0.015em;
  margin: 0;
  color: ${colors.ink};

  span {
    display: inline-block;
    font: 400 11px ${fonts.mono};
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${colors.muted};
    margin-left: 16px;
    vertical-align: middle;
  }
`;

export const PastEventsToggle = styled.span<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.muted};

  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

export const PastEventsContent = styled.div<{ $isExpanded: boolean }>`
  display: ${props => props.$isExpanded ? 'block' : 'none'};
  margin-top: 32px;
`;

export const PastEventRow = styled.article`
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr) auto;
  gap: 28px;
  padding: 18px 0;
  border-top: 1px solid ${colors.rule};
  align-items: start;
  opacity: 0.7;

  &:first-child {
    border-top: 1px solid ${colors.ink};
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;
